import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { notification } from "antd";
import CryptoJS from "crypto-js";
import Request from "../../../helpers/request.service";
import { authUrl } from "../../../constants/apiBaseUrl";
import EmailStep from "./components/EmailStep";
import OtpStep from "./components/OtpStep";
import NewPasswordStep from "./components/NewPasswordStep";

const RESEND_COOLDOWN_SECONDS = 60;

/**
 * ForgotPassword orchestrator.
 *
 * Drives the three-step OTP wizard: email → OTP → new password. Owns the
 * verification-token returned by /verify so /complete can consume it.
 * Maps BE error codes (AUTH_020..AUTH_027 + GEN_008 rate-limit) into the
 * inline error / cooldown UI surfaced by each step.
 */
export default function ForgotPassword() {
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [otpVerificationToken, setOtpVerificationToken] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [emailError, setEmailError] = useState(null);
  const [otpError, setOtpError] = useState(null);
  const [otpAttemptsRemaining, setOtpAttemptsRemaining] = useState(null);
  const [passwordError, setPasswordError] = useState(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [otpResetSignal, setOtpResetSignal] = useState(0);
  const cooldownTimer = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    return () => {
      if (cooldownTimer.current) clearInterval(cooldownTimer.current);
    };
  }, []);

  const startCooldown = useCallback((seconds = RESEND_COOLDOWN_SECONDS) => {
    setResendCooldown(seconds);
    if (cooldownTimer.current) clearInterval(cooldownTimer.current);
    cooldownTimer.current = setInterval(() => {
      setResendCooldown((s) => {
        if (s <= 1) {
          clearInterval(cooldownTimer.current);
          cooldownTimer.current = null;
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  }, []);

  const handleStartSubmit = useCallback(
    async (submittedEmail) => {
      setSubmitting(true);
      setEmailError(null);
      try {
        await Request.postJson(authUrl.forgot_password_start, { email: submittedEmail });
        setEmail(submittedEmail);
        setStep("otp");
        setOtpResetSignal((n) => n + 1);
        startCooldown();
      } catch (err) {
        const data = err?.response?.data;
        const code = data?.data?.code || data?.code;
        const details = data?.data?.details || {};
        if (code === "GEN_008") {
          const seconds = Math.ceil((details.retryAfterMillis || 0) / 1000);
          setEmailError(
            seconds > 0
              ? `Too many requests. Try again in ${seconds}s.`
              : data?.message || "Too many requests. Please try again later.",
          );
        } else {
          setEmailError(data?.message || "Could not send code. Please try again.");
        }
      } finally {
        setSubmitting(false);
      }
    },
    [startCooldown],
  );

  const handleOtpSubmit = useCallback(
    async (otp) => {
      setSubmitting(true);
      setOtpError(null);
      setOtpAttemptsRemaining(null);
      try {
        const res = await Request.postJson(authUrl.forgot_password_verify, { email, otp });
        const token = res?.data?.data?.otpVerificationToken;
        if (!token) {
          setOtpError("Unexpected response from server.");
          return;
        }
        setOtpVerificationToken(token);
        setStep("password");
      } catch (err) {
        const data = err?.response?.data;
        const code = data?.data?.code || data?.code;
        const details = data?.data?.details || {};
        if (code === "AUTH_020") {
          setOtpError(data?.message || "Incorrect OTP. Please try again.");
          if (typeof details.attemptsRemaining === "number") {
            setOtpAttemptsRemaining(details.attemptsRemaining);
          }
        } else if (code === "AUTH_021") {
          setOtpError("Code expired. Please resend a new code.");
        } else if (code === "AUTH_022") {
          setOtpError("Too many wrong attempts. Please resend a new code.");
        } else {
          setOtpError(data?.message || "Could not verify code. Please try again.");
        }
      } finally {
        setSubmitting(false);
      }
    },
    [email],
  );

  const handleResend = useCallback(async () => {
    if (resendCooldown > 0) return;
    setResending(true);
    setOtpError(null);
    try {
      await Request.postJson(authUrl.forgot_password_resend, { email });
      setOtpResetSignal((n) => n + 1);
      setOtpAttemptsRemaining(null);
      startCooldown();
      notification.success({ message: "Code resent", description: `A new code was sent to ${email}.`, duration: 3 });
    } catch (err) {
      const data = err?.response?.data;
      const code = data?.data?.code || data?.code;
      const details = data?.data?.details || {};
      if (code === "GEN_008") {
        const seconds = Math.ceil((details.retryAfterMillis || 0) / 1000);
        setOtpError(
          seconds > 0
            ? `Too many requests. Try again in ${seconds}s.`
            : data?.message || "Too many requests. Please try again later.",
        );
        if (seconds > 0) startCooldown(seconds);
      } else {
        setOtpError(data?.message || "Could not resend code. Please try again.");
      }
    } finally {
      setResending(false);
    }
  }, [email, resendCooldown, startCooldown]);

  const handlePasswordSubmit = useCallback(
    async (newPassword) => {
      if (!otpVerificationToken) {
        setPasswordError("Session expired. Please restart the reset.");
        return;
      }
      setSubmitting(true);
      setPasswordError(null);
      try {
        const hash = CryptoJS.SHA512(newPassword).toString(CryptoJS.enc.Hex);
        await Request.postJson(authUrl.forgot_password_complete, {
          otpVerificationToken,
          newPassword: hash,
        });
        notification.success({
          message: "Password updated",
          description: "Your password has been reset. Please sign in with the new password.",
          duration: 4,
        });
        navigate("/login", { replace: true });
      } catch (err) {
        const data = err?.response?.data;
        const code = data?.data?.code || data?.code;
        if (code === "AUTH_025") {
          setPasswordError("Verification token is invalid or expired. Restart the reset.");
        } else if (code === "AUTH_026" || code === "GEN_007") {
          setPasswordError(data?.message || "New password must not match any of your last 5 passwords.");
        } else {
          setPasswordError(data?.message || "Could not reset password. Please try again.");
        }
      } finally {
        setSubmitting(false);
      }
    },
    [navigate, otpVerificationToken],
  );

  const handleBack = useCallback(() => {
    setStep("email");
    setOtpError(null);
    setOtpAttemptsRemaining(null);
  }, []);

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-4">
      <div className="w-full max-w-[460px] bg-milk border border-border rounded-2xl p-8">
        <div className="text-center">
          <div className="font-display text-4xl text-cocoa">
            <span className="text-honey">✿</span> Sulsa <span className="text-honey">✿</span>
          </div>
          <div className="text-honey italic text-sm mt-1">Reset your password</div>
        </div>

        {step === "email" && (
          <EmailStep
            initialEmail={email}
            submitting={submitting}
            errorMessage={emailError}
            onSubmit={handleStartSubmit}
          />
        )}

        {step === "otp" && (
          <OtpStep
            email={email}
            submitting={submitting}
            resending={resending}
            resendCooldown={resendCooldown}
            errorMessage={otpError}
            attemptsRemaining={otpAttemptsRemaining}
            onSubmit={handleOtpSubmit}
            onResend={handleResend}
            onBack={handleBack}
            resetSignal={otpResetSignal}
          />
        )}

        {step === "password" && (
          <NewPasswordStep
            submitting={submitting}
            errorMessage={passwordError}
            onSubmit={handlePasswordSubmit}
          />
        )}

        <div className="mt-6 pt-5 border-t border-border text-center text-xs text-mahogany">
          <Link to="/login" className="hover:text-cocoa underline-offset-2 hover:underline">
            Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
